import {
  createContext,
  useEffect,
  useState,
  useContext,
  ReactNode,
} from 'react';

interface DismissableContextType {
  sessionDismissed: Record<string, boolean>;
  persistentDismissed: Record<string, boolean>;
  dismissAlert: (cardId: string, persistent?: boolean) => void;
  resetAlert: (cardId: string, persistent?: boolean) => void;
}

const DismissableContext = createContext<DismissableContextType>({
  sessionDismissed: {},
  persistentDismissed: {},
  dismissAlert: () => {},
  resetAlert: () => {},
});

const safeParse = (value: string | null): Record<string, boolean> => {
  if (!value) return {};
  try {
    const parsed = JSON.parse(value);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      !Array.isArray(parsed)
    ) {
      return Object.fromEntries(
        Object.entries(parsed)
          .filter(([, v]) => typeof v === 'boolean')
          .map(([k, v]) => [k, v as boolean])
      );
    }
  } catch {
    return {};
  }
  return {};
};

export const DismissProvider = ({ children }: { children: ReactNode }) => {
  const [sessionDismissed, setSessionDismissed] = useState<
    Record<string, boolean>
  >(() => {
    return safeParse(sessionStorage.getItem('dismissedSessionCards'));
  });
  const [persistentDismissed, setPersistentDismissed] = useState<
    Record<string, boolean>
  >(() => {
    return safeParse(localStorage.getItem('dismissedPersistentCards'));
  });

  useEffect(() => {
    sessionStorage.setItem(
      'dismissedSessionCards',
      JSON.stringify(sessionDismissed)
    );
  }, [sessionDismissed]);

  useEffect(() => {
    localStorage.setItem(
      'dismissedPersistentCards',
      JSON.stringify(persistentDismissed)
    );
  }, [persistentDismissed]);

  const dismissAlert = (cardId: string, persistent = false) => {
    if (persistent) {
      setPersistentDismissed((prev) => {
        const updated = { ...prev, [cardId]: true };
        localStorage.setItem(
          'dismissedPersistentCards',
          JSON.stringify(updated)
        );
        return updated;
      });
    } else {
      setSessionDismissed((prev) => {
        const updated = { ...prev, [cardId]: true };
        sessionStorage.setItem(
          'dismissedSessionCards',
          JSON.stringify(updated)
        );
        return updated;
      });
    }
  };

  const resetAlert = (cardId: string, persistent = false) => {
    if (persistent) {
      setPersistentDismissed((prev) => {
        const updated = { ...prev, [cardId]: false };
        localStorage.setItem(
          'dismissedPersistentCards',
          JSON.stringify(updated)
        );
        return updated;
      });
    } else {
      setSessionDismissed((prev) => {
        const updated = { ...prev, [cardId]: false };
        sessionStorage.setItem(
          'dismissedSessionCards',
          JSON.stringify(updated)
        );
        return updated;
      });
    }
  };

  return (
    <DismissableContext.Provider
      value={{
        sessionDismissed,
        persistentDismissed,
        dismissAlert,
        resetAlert,
      }}
    >
      {children}
    </DismissableContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useDismissable = () => useContext(DismissableContext);
